/* eslint-disable */
import axios from 'axios';
import { getRefreshToken, getToken, isAuthenticated } from './auth';
import { queryParamsHelper } from './query';

export const baseUrl =
  process.env.REACT_APP_API_BASE_URL ||
  'https://api.dev.facilitybills.com/api/v1/';

const Api = axios.create({
  baseURL: baseUrl
});

// Request interceptor for API calls
Api.interceptors.request.use(
  async (config) => {
    config.headers = {
      Authorization: isAuthenticated() ? `Bearer ${getToken()}` : ''
    };
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

// Response interceptor for API calls
Api.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    const originalRequest = error?.config;

    if (error?.response?.status === 401 && originalRequest?._retry) {
      const rToken = getRefreshToken();
      if (rToken) {
        const access_token = await refreshToken(rToken);
        axios.defaults.headers.common['Authorization'] =
          'Bearer ' + access_token;
      }
      return Api(originalRequest);
    }
    return Promise.reject(error);
  }
);

function refreshToken(token) {
  return Api.post('/auth/token/refresh/', {
    refresh: token
  });
}

const EXTENSION_BY_CONTENT_TYPE = {
  'text/csv': 'csv',
  'application/pdf': 'pdf',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx'
};

// The API builds names with strftime('%x-%X'), so they arrive full of
// slashes and colons that browsers will not accept in a filename.
const sanitizeFilename = (filename) => filename.replace(/[/\\:*?"<>|]/g, '-');

const filenameFromHeaders = (headers) => {
  const disposition = headers?.['content-disposition'];
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ? sanitizeFilename(decodeURIComponent(match[1])) : null;
};

const fallbackFilename = (name, contentType) => {
  const extension = EXTENSION_BY_CONTENT_TYPE[contentType?.split(';')[0]];
  const date = new Date().toLocaleDateString('sv').split('T')[0];
  return `${name}-${date}${extension ? `.${extension}` : ''}`;
};

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// Errors on a blob request come back as a Blob too, so unwrap them into the
// plain object handleApiError expects. It reads err.response.data blindly, so
// a request that never got a response needs one filled in as well.
const readBlobError = async (error) => {
  const err = error || new Error('Download failed');
  const data = err?.response?.data;

  if (!data) {
    err.response = { ...err.response, data: { detail: err.message } };
    return err;
  }
  if (!(data instanceof Blob)) return err;

  try {
    err.response.data = JSON.parse(await data.text());
  } catch {
    err.response.data = { detail: 'Unable to download the file' };
  }
  return err;
};

/**
 * Download an export through the authenticated Api instance and hand the blob
 * to the browser. Preferred over building a link with the token in the URL:
 * the JWT stays in the Authorization header instead of leaking into browser
 * history, referrers and server access logs.
 */
export const downloadFile = async (path, queryParams = {}, name = 'export') => {
  const params = { ...queryParams };
  delete params.filterValue;

  try {
    const { data, headers } = await Api.get(
      `${path}${queryParamsHelper(params)}`,
      { responseType: 'blob' }
    );
    saveBlob(
      data,
      filenameFromHeaders(headers) || fallbackFilename(name, data?.type)
    );
  } catch (error) {
    throw await readBlobError(error);
  }
};

export default Api;
